import { useCallback } from "react";
import {
  Check,
  ChevronRight,
  ClipboardList,
  Images,
  Map,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { coverImage, recordPhotoCount } from "@/lib/record-images";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { RecordSyncBadge } from "@/components/records/record-sync-badge/record-sync-badge";
import { thumbAssetKey } from "@/offline/asset-keys";
import { useOnlineStatus } from "@/offline/online-status";
import { useJustSynced } from "@/hooks/use-just-synced";
import { useLongPress } from "@/hooks/use-long-press";
import {
  SWIPE_ACTION_WIDTH,
  useSwipeAction,
} from "@/hooks/use-swipe-action";
import { useSwipeHint } from "@/hooks/use-swipe-hint";
import type { PendingRecordTile } from "@/hooks/use-pending-records";
import type { ProjectRecord } from "@/types/api";

interface PhotoListItemProps {
  record: ProjectRecord;
  /**
   * The record's Remarks value, already resolved against the project's
   * metadata fields by [[photo-list.spec]] — the row renders a muted
   * placeholder when it is missing or blank.
   */
  description?: string;
  /** Resolved plan name, or null for an unpinned record. */
  planName?: string | null;
  /**
   * When set, this row renders an outbox record (a local draft that has not yet
   * synced): the thumbnail comes from the locally captured blob and a sync
   * badge is shown. See [[offline-record-outbox]].
   */
  outbox?: PendingRecordTile;
  /** Tap-to-retry handler for a `failed` outbox row. */
  onRetryOutbox?: (tile: PendingRecordTile) => void;
  /** Append-outbox state for a **synced** record with "+ Add" photos in
   * flight. Unlike `outbox`, it never makes the row inert. */
  appendState?: "pending" | "failed";
  onClick?: (record: ProjectRecord) => void;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onLongPress?: (record: ProjectRecord) => void;
  /**
   * Raise an issue against this record. When provided, the row can be dragged
   * left to reveal a "Raise issue" action and committing the drag calls this —
   * the mobile shortcut to the same sheet the record viewer opens. Omitted on
   * desktop (no thumb to swipe with) and wherever the viewer cannot raise an
   * issue either. See [[photo-list-item.spec]] US-005.
   */
  onRaiseIssue?: (record: ProjectRecord) => void;
  /**
   * This row is the list's first swipeable one, so it plays the daily peek
   * that shows rows swipe. The list passes it to a single row; the row still
   * checks the device has not seen the hint. See [[photo-list-item.spec]]
   * US-006.
   */
  swipeHint?: boolean;
  /**
   * Whether this record is the one open in the record side panel beside the
   * gallery — marked so you can see which photo the panel is showing. See
   * [[records-view.spec]] US-006.
   */
  isActive?: boolean;
}

/**
 * One record as a row: thumbnail, description and plan. The counterpart
 * to [[photo-grid-item.spec]] for list view, and deliberately shaped like
 * [[issue-item.spec]] so the two lists read as one surface.
 */
export function PhotoListItem({
  record,
  description,
  planName,
  outbox,
  onRetryOutbox,
  appendState,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onLongPress,
  onRaiseIssue,
  swipeHint = false,
  isActive = false,
}: PhotoListItemProps) {
  // The row thumbnail is the record's cover — `images[0]` under the v2.0 read,
  // the legacy `image` otherwise — via the [[record-images]] seam.
  const cover = coverImage(record);
  const src = cover?.thumbnail ?? cover?.image ?? "";
  const photoCount = recordPhotoCount(record);
  const isMultiPhoto = photoCount > 1;

  // Create-outbox state: only a *creating* (unsynced) row is dimmed / inert /
  // tap-to-retry — its server id isn't placed yet.
  const isFailed = outbox?.status === "failed";
  const isPending = outbox?.status === "pending";
  // Append-outbox state for a SYNCED record. Same badges, but the row stays
  // fully tappable — append retry lives in the viewer. See
  // [[append-photo-outbox]].
  const isAppendPending = !outbox && appendState === "pending";
  const isAppendFailed = !outbox && appendState === "failed";
  const showPendingBadge = isPending || isAppendPending;
  const showFailedBadge = isFailed || isAppendFailed;

  const isOnline = useOnlineStatus();
  // Pending AND online means mid-upload: the record has no server id yet, so
  // the row must not open the editor. The offline "Queued" draft stays
  // interactive. See [[offline-record-outbox]].
  const isSyncing = isPending && isOnline;
  // The open record (side panel) and a selected record (select tool) share
  // one treatment: muted fill + tertiary left edge.
  const isHighlighted = isActive || (isSelectionMode && isSelected);
  const isInert = isSyncing && !isSelectionMode;
  const isJustSynced = useJustSynced(record.id);

  const { longPressHandlers, consumeClick } = useLongPress(record, onLongPress);

  // Selection mode owns the row's taps and drags, so the swipe stands down
  // while it is active. See [[photo-list-item.spec]] US-005.
  const swipeEnabled = !!onRaiseIssue && !isSelectionMode;
  const {
    offset: swipeOffset,
    isDragging,
    handlers: swipeHandlers,
    consumeClick: consumeSwipeClick,
  } = useSwipeAction({
    enabled: swipeEnabled,
    onTrigger: () => onRaiseIssue?.(record),
  });
  const { ref: hintRef, isHinting } = useSwipeHint({
    enabled: swipeHint && swipeEnabled,
    list: "records",
  });

  // Both gestures listen on touch, so the row composes them by hand rather
  // than spreading two objects that would clobber each other's handlers.
  const touchHandlers = {
    onTouchStart: (event: React.TouchEvent) => {
      longPressHandlers.onTouchStart(event);
      swipeHandlers.onTouchStart(event);
    },
    onTouchMove: (event: React.TouchEvent) => {
      longPressHandlers.onTouchMove(event);
      swipeHandlers.onTouchMove(event);
    },
    onTouchEnd: () => {
      longPressHandlers.onTouchEnd();
      swipeHandlers.onTouchEnd();
    },
    onTouchCancel: () => {
      longPressHandlers.onTouchCancel();
      swipeHandlers.onTouchCancel();
    },
  };

  const handleClick = useCallback(() => {
    // Swallow the click that trails a swipe — the drag already did something.
    if (consumeSwipeClick()) return;
    // Swallow the click a touch device synthesises after a long-press, so one
    // press doesn't both select and open the record.
    if (consumeClick()) return;
    if (isFailed && !isSelectionMode && outbox) {
      onRetryOutbox?.(outbox);
      return;
    }
    if (isSyncing && !isSelectionMode) return;
    onClick?.(record);
  }, [
    consumeSwipeClick,
    consumeClick,
    isFailed,
    isSyncing,
    isSelectionMode,
    outbox,
    onRetryOutbox,
    onClick,
    record,
  ]);

  const trimmedDescription = description?.trim();

  return (
    <div className="relative h-full overflow-hidden bg-card">
      {/* The action revealed behind the row. Decorative while closed — the
          gesture is what commits it, so it takes no tap of its own. It is also
          what the peek hint shows a sliver of. */}
      {swipeEnabled && (swipeOffset < 0 || isHinting) && (
        <div
          aria-hidden="true"
          data-testid="swipe-raise-issue"
          className="absolute inset-y-0 right-0 flex items-center justify-center gap-1.5 bg-tertiary px-3 text-xs font-medium text-tertiary-foreground"
          style={{ width: SWIPE_ACTION_WIDTH }}
        >
          <ClipboardList className="h-4 w-4" />
          Raise issue
        </div>
      )}
      <button
        ref={hintRef}
        type="button"
        onClick={handleClick}
        {...touchHandlers}
        onContextMenu={(e) => e.preventDefault()}
        aria-pressed={isSelectionMode ? isSelected : undefined}
        aria-current={isActive || undefined}
        className={cn(
          "relative flex w-full items-center gap-3 bg-card p-1.5 text-left transition-colors duration-100 active:bg-card-pressed",
          isHighlighted && "bg-muted shadow-[inset_3px_0_0_var(--tertiary)]",
          // Pointer hover, as on the issues list: a muted wash in light, a
          // lighter fill in dark ([[theming]] § Card rows lift on hover and
          // press). Not on a highlighted row — its muted fill and left edge
          // already mark it — nor on an inert syncing row.
          !isHighlighted && !isInert && "hover:bg-card-hover",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          isInert && "cursor-default",
          // Springs back after a release; follows the finger during the drag.
          !isDragging && "transition-transform duration-200",
          isHinting && "swipe-hint-nudge",
        )}
        style={{
          WebkitTouchCallout: "none",
          // Vertical scrolling stays with the list; horizontal is ours.
          touchAction: swipeEnabled ? "pan-y" : undefined,
          transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
        }}
      >
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted">
          {outbox ? (
            // Outbox row: a plain <img> off the in-hand object URL — no server
            // URL, no offline-image cache — so it renders fully offline.
            <img
              src={outbox.thumbnailUrl}
              alt=""
              decoding="async"
              className={cn(
                "h-full w-full object-cover",
                isSyncing && "opacity-60",
              )}
            />
          ) : (
            src && (
              <OfflineImage
                assetKey={thumbAssetKey(record)}
                src={src}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            )
          )}
          {isMultiPhoto && (
            <div
              className="pointer-events-none absolute left-0.5 top-0.5 z-10"
              data-testid="photo-count-badge"
            >
              <span className="inline-flex items-center gap-0.5 rounded-full bg-black/60 px-1 py-0.5 text-[10px] font-medium leading-none text-white">
                <Images className="h-2.5 w-2.5" />
                {photoCount}
              </span>
            </div>
          )}
          {showPendingBadge && (
            <RecordSyncBadge
              state="pending"
              className="absolute bottom-0.5 left-0.5"
            />
          )}
          {showFailedBadge && (
            <RecordSyncBadge
              state="failed"
              isAppend={isAppendFailed}
              className="absolute bottom-0.5 left-0.5"
            />
          )}
          {isJustSynced && (
            <RecordSyncBadge
              state="synced"
              className="absolute bottom-0.5 left-0.5"
            />
          )}
          {isSelectionMode && (
            <div className="pointer-events-none absolute right-0.5 top-0.5 z-10">
              {isSelected ? (
                // Tertiary (accent) bg with white border per
                // [[selection-mode.spec]] § Layout — same treatment as the tile.
                <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-tertiary text-tertiary-foreground shadow-sm">
                  <Check className="h-3 w-3" />
                </div>
              ) : (
                <div
                  className="h-5 w-5 rounded-full border-2"
                  style={{
                    backgroundColor: "rgba(43, 43, 43, 0.4)",
                    borderColor: "rgba(255, 255, 255, 0.9)",
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span
            className={cn(
              "truncate text-sm font-medium tracking-[-0.005em]",
              !trimmedDescription && "font-normal text-muted-foreground/70",
            )}
          >
            {trimmedDescription || "No description"}
          </span>
          {/* The plan is the whole second line; an unpinned record simply has
              none, and the row keeps its height from the thumbnail. */}
          {planName && (
            <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <Map className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{planName}</span>
            </span>
          )}
        </div>

        {!isSelectionMode && (
          <ChevronRight
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </button>
    </div>
  );
}

export function PhotoListItemSkeleton() {
  return (
    <div className="flex items-center gap-3 bg-card p-1.5">
      <div className="h-16 w-16 shrink-0 animate-pulse rounded-md bg-muted" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-3.5 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
